// ========= Copyright 2025-2026 @ Eigent.ai All Rights Reserved. =========
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//     http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.
// ========= Copyright 2025-2026 @ Eigent.ai All Rights Reserved. =========

import { ResponsivePanel } from '@/components/remote/ResponsivePanel';
import { Button } from '@/components/ui/button';
import { MorphIcon } from '@/components/ui/morph-icon';
import { cn } from '@/lib/utils';
import {
  confirmSpaceBundleInstall,
  getCloudMemoryScope,
  getSpaceEnvironment,
  getWorkspaceBundleRevision,
  installSpaceBundle,
  isRemoteControlTargetReady,
  listWorkspaceBundles,
  upgradeSpaceBundle,
  type CloudMemoryScope,
  type CloudMemoryScopeType,
  type RemoteControlSession,
  type RemoteControlTarget,
  type SpaceEnvironment,
  type WorkspaceBundleRevision,
  type WorkspaceBundleSummary,
} from '@web/api/remoteControl';
import {
  Ban,
  Brain,
  Clock3,
  Loader2,
  PackageOpen,
  RefreshCw,
  ShieldCheck,
  ShieldX,
  SkipForward,
  Wifi,
  WifiOff,
  X,
} from 'lucide';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

function formatDate(value: string | null | undefined): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

function remoteStateLabel(
  session: RemoteControlSession | null,
  target: RemoteControlTarget | null
): string {
  if (!session) return '';
  if (session.status !== 'active') return 'Remote link inactive';
  if (session.bridge_status !== 'online') return 'Desktop offline';
  if (!isRemoteControlTargetReady(target)) return 'No active project';
  return 'Desktop online';
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

const REQUIRED_BUNDLE_PROTOCOL_CAPABILITIES = [
  'bundle.asset.integrity.v1',
  'bundle.install.review.v1',
] as const;

export function supportsBundleReviewProtocol(
  environment: SpaceEnvironment | null | undefined
): boolean {
  const available = new Set(environment?.protocol_capabilities || []);
  return REQUIRED_BUNDLE_PROTOCOL_CAPABILITIES.every((item) =>
    available.has(item)
  );
}

export type BundleInstallAction =
  | 'install_proposal'
  | 'confirm_proposal'
  | 'upgrade_proposal'
  | 'installed'
  | 'resolve_other_proposal'
  | 'unavailable';

export function bundleInstallAction(
  environment: SpaceEnvironment | null | undefined,
  revision: Pick<WorkspaceBundleRevision, 'id'> | null | undefined
): BundleInstallAction {
  if (!revision) return 'unavailable';
  const current = environment?.installation;
  if (!current) return 'install_proposal';
  const sameRevision = current.installed_revision_id === revision.id;
  if (current.state === 'proposed') {
    return sameRevision ? 'confirm_proposal' : 'resolve_other_proposal';
  }
  return sameRevision ? 'installed' : 'upgrade_proposal';
}

export function bundleRequirements(revision: unknown): {
  connectors: number;
  localFolders: number;
  executableActions: number;
  assets: number;
} {
  const revisionRecord = asRecord(revision);
  const manifest = asRecord(revisionRecord.manifest);
  const spec = asRecord(manifest.spec);
  const connectors = Array.isArray(spec.connectors)
    ? spec.connectors.filter((item) => Object.keys(asRecord(item)).length > 0)
    : [];
  const context = Array.isArray(spec.context)
    ? spec.context.map(asRecord).filter((item) => Object.keys(item).length > 0)
    : [];
  const skills = Array.isArray(spec.skills)
    ? spec.skills.map(asRecord).filter((item) => Object.keys(item).length > 0)
    : [];
  const mcpServers = Array.isArray(spec.mcpServers)
    ? spec.mcpServers.filter((item) => Object.keys(asRecord(item)).length > 0)
    : [];
  return {
    connectors: connectors.length,
    localFolders: context.filter((item) => item.kind === 'local_path_slot')
      .length,
    executableActions:
      skills.filter((item) => String(item.ref || '').startsWith('bundle://'))
        .length + mcpServers.length,
    assets: Array.isArray(revisionRecord.assets)
      ? revisionRecord.assets.length
      : 0,
  };
}

export type BundleConnectionRequirement = {
  slotId: string;
  connectorId: string;
  requiredGrants: string[];
};

export function bundleConnectionRequirements(
  revision: unknown
): BundleConnectionRequirement[] {
  const revisionRecord = asRecord(revision);
  const manifest = asRecord(revisionRecord.manifest);
  const spec = asRecord(manifest.spec);
  if (!Array.isArray(spec.connectors)) return [];
  return spec.connectors
    .map(asRecord)
    .map((item) => ({
      slotId: String(item.connectionSlot || ''),
      connectorId: String(item.connector || ''),
      requiredGrants: Array.isArray(item.requiredGrants)
        ? item.requiredGrants.map(String).sort()
        : [],
    }))
    .filter((item) => item.slotId && item.connectorId);
}

interface RemoteSettingsPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  session: RemoteControlSession | null;
  target: RemoteControlTarget | null;
  controlLoading: string | null;
  onSkip: () => void;
  onStop: () => void;
  onExtend: () => void;
  onRevoke: () => void;
  ready: boolean;
  bridgeOnline: boolean;
  sessionId: string;
  linkToken: string;
}

export function RemoteSettingsPanel({
  open,
  onOpenChange,
  session,
  target,
  controlLoading,
  onSkip,
  onStop,
  onExtend,
  onRevoke,
  ready,
  bridgeOnline,
  sessionId,
  linkToken,
}: RemoteSettingsPanelProps) {
  const [bundles, setBundles] = useState<WorkspaceBundleSummary[]>([]);
  const [environment, setEnvironment] = useState<SpaceEnvironment | null>(null);
  const [revision, setRevision] = useState<WorkspaceBundleRevision | null>(
    null
  );
  const [bundleLoading, setBundleLoading] = useState(false);
  const [bundleAction, setBundleAction] = useState(false);
  const [bundleError, setBundleError] = useState('');
  const [memoryScopeType, setMemoryScopeType] =
    useState<CloudMemoryScopeType>('project');
  const [memoryScope, setMemoryScope] = useState<CloudMemoryScope | null>(null);
  const [memoryLoading, setMemoryLoading] = useState(false);
  const [memoryError, setMemoryError] = useState('');
  const bundleRequestGeneration = useRef(0);
  const memoryRequestGeneration = useRef(0);
  const spaceId = session?.space_id || '';
  const projectId = target?.project_id || '';

  const loadBundleState = useCallback(async () => {
    const requestGeneration = ++bundleRequestGeneration.current;
    if (!sessionId || !linkToken || !spaceId) {
      setBundles([]);
      setEnvironment(null);
      setRevision(null);
      setBundleLoading(false);
      return;
    }
    setBundleLoading(true);
    setBundleError('');
    try {
      const [available, current] = await Promise.all([
        listWorkspaceBundles(sessionId, linkToken),
        getSpaceEnvironment(sessionId, linkToken, spaceId),
      ]);
      const published = available.filter(
        (item) => item.latest_published_revision_id
      );
      const selected =
        published.find((item) => item.id === current.installation?.bundle_id) ||
        published[0];
      const revisionId =
        selected &&
        current.installation &&
        selected.id === current.installation.bundle_id
          ? current.installation.installed_revision_id
          : selected?.latest_published_revision_id;
      const selectedRevision =
        selected && revisionId
          ? await getWorkspaceBundleRevision(
              sessionId,
              linkToken,
              selected.id,
              revisionId
            )
          : null;
      if (requestGeneration !== bundleRequestGeneration.current) return;
      setBundles(published);
      setEnvironment(current);
      setRevision(selectedRevision);
    } catch (error) {
      if (requestGeneration !== bundleRequestGeneration.current) return;
      setBundleError(
        error instanceof Error
          ? error.message
          : 'Unable to load Workspace Bundles'
      );
    } finally {
      if (requestGeneration === bundleRequestGeneration.current) {
        setBundleLoading(false);
      }
    }
  }, [linkToken, sessionId, spaceId]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onOpenChange(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onOpenChange]);

  useEffect(() => {
    if (open) {
      void loadBundleState();
      return;
    }
    bundleRequestGeneration.current += 1;
    setBundleLoading(false);
  }, [loadBundleState, open]);

  const loadMemoryScope = useCallback(async () => {
    const requestGeneration = ++memoryRequestGeneration.current;
    const scopeId =
      memoryScopeType === 'project'
        ? projectId
        : memoryScopeType === 'space'
          ? spaceId
          : undefined;
    if (
      !open ||
      !sessionId ||
      !linkToken ||
      (memoryScopeType !== 'user' && !scopeId)
    ) {
      if (requestGeneration === memoryRequestGeneration.current) {
        setMemoryScope(null);
        setMemoryLoading(false);
      }
      return;
    }
    setMemoryScope(null);
    setMemoryLoading(true);
    setMemoryError('');
    try {
      const response = await getCloudMemoryScope(
        sessionId,
        linkToken,
        memoryScopeType,
        scopeId
      );
      if (requestGeneration === memoryRequestGeneration.current) {
        setMemoryScope(response);
      }
    } catch (error) {
      if (requestGeneration === memoryRequestGeneration.current) {
        setMemoryScope(null);
        setMemoryError(
          error instanceof Error
            ? error.message
            : 'Memory has not synchronized yet'
        );
      }
    } finally {
      if (requestGeneration === memoryRequestGeneration.current) {
        setMemoryLoading(false);
      }
    }
  }, [linkToken, memoryScopeType, open, projectId, sessionId, spaceId]);

  useEffect(() => {
    void loadMemoryScope();
  }, [loadMemoryScope]);

  const requirements = useMemo(() => bundleRequirements(revision), [revision]);
  const connectionRequirements = useMemo(
    () => bundleConnectionRequirements(revision),
    [revision]
  );
  const bundleProtocolReady = supportsBundleReviewProtocol(environment);
  const reviewedBundleAction = bundleInstallAction(environment, revision);
  const anotherProposalPending =
    reviewedBundleAction === 'resolve_other_proposal';

  const selectBundle = useCallback(
    async (bundle: WorkspaceBundleSummary) => {
      if (!bundle.latest_published_revision_id) return;
      const requestGeneration = ++bundleRequestGeneration.current;
      setBundleLoading(true);
      setBundleError('');
      try {
        const selectedRevision = await getWorkspaceBundleRevision(
          sessionId,
          linkToken,
          bundle.id,
          bundle.latest_published_revision_id
        );
        if (requestGeneration !== bundleRequestGeneration.current) return;
        setRevision(selectedRevision);
      } catch (error) {
        if (requestGeneration !== bundleRequestGeneration.current) return;
        setBundleError(
          error instanceof Error ? error.message : 'Unable to review Bundle'
        );
      } finally {
        if (requestGeneration === bundleRequestGeneration.current) {
          setBundleLoading(false);
        }
      }
    },
    [linkToken, sessionId]
  );

  const installReviewedBundle = useCallback(async () => {
    if (!revision || !spaceId) return;
    setBundleAction(true);
    setBundleError('');
    try {
      if (!supportsBundleReviewProtocol(environment)) {
        throw new Error(
          'The Cloud service must be upgraded before Bundle installation can change.'
        );
      }
      const current = environment?.installation;
      if (current && current.bundle_id !== revision.bundle_id) {
        throw new Error(
          'This Space already uses another Bundle. Replace must be reviewed in Desktop.'
        );
      }
      if (reviewedBundleAction === 'confirm_proposal' && current) {
        await confirmSpaceBundleInstall(
          sessionId,
          linkToken,
          spaceId,
          current,
          revision
        );
      } else if (reviewedBundleAction === 'upgrade_proposal' && current) {
        await upgradeSpaceBundle(
          sessionId,
          linkToken,
          spaceId,
          revision.id,
          current
        );
      } else if (reviewedBundleAction === 'install_proposal') {
        await installSpaceBundle(
          sessionId,
          linkToken,
          spaceId,
          revision.bundle_id,
          revision.id
        );
      } else if (reviewedBundleAction === 'resolve_other_proposal') {
        throw new Error(
          'Confirm or remove the current Bundle proposal before selecting another revision.'
        );
      }
      await loadBundleState();
    } catch (error) {
      setBundleError(
        error instanceof Error ? error.message : 'Unable to install Bundle'
      );
    } finally {
      setBundleAction(false);
    }
  }, [
    environment,
    linkToken,
    loadBundleState,
    revision,
    reviewedBundleAction,
    sessionId,
    spaceId,
  ]);

  return (
    <ResponsivePanel
      open={open}
      onOpenChange={onOpenChange}
      side="right"
      motionKey="remote-settings-panel"
      ariaLabelledBy="remote-settings-panel-title"
    >
      <h2 id="remote-settings-panel-title" className="sr-only">
        Remote control settings
      </h2>

      <div className="flex h-full min-h-0 flex-col overflow-hidden">
        {/* Header */}
        <header className="flex h-14 shrink-0 items-center justify-between px-4">
          <span className="text-base font-semibold text-ds-text-neutral-default-default">
            Settings
          </span>
          <Button
            variant="ghost"
            size="sm"
            buttonContent="icon-only"
            buttonRadius="full"
            aria-label="Close settings panel"
            onClick={() => onOpenChange(false)}
          >
            <MorphIcon icon={X} className="h-4 w-4" />
          </Button>
        </header>

        <div className="flex flex-col gap-5 overflow-y-auto px-4 pb-4">
          {/* Status card */}
          <div className="flex flex-col items-center gap-3 rounded-2xl border border-solid border-ds-border-neutral-subtle-default bg-ds-bg-neutral-subtle-default p-5 text-center">
            <span
              className={cn(
                'flex h-14 w-14 items-center justify-center rounded-2xl',
                bridgeOnline
                  ? 'bg-ds-bg-status-completed-subtle-default text-ds-text-status-completed-default-default'
                  : 'bg-ds-bg-neutral-default-default text-ds-icon-neutral-muted-default'
              )}
            >
              <MorphIcon
                icon={bridgeOnline ? Wifi : WifiOff}
                className="h-6 w-6"
              />
            </span>
            <div className="flex flex-col gap-1">
              <span className="text-base font-semibold text-ds-text-neutral-default-default">
                {remoteStateLabel(session, target)}
              </span>
              {session?.expires_at && (
                <span className="text-body-sm text-ds-text-neutral-muted-default">
                  Expires {formatDate(session.expires_at)}
                </span>
              )}
            </div>
          </div>

          {/* Read-only Cloud Memory replica. Canonical History is never exposed here. */}
          <section className="flex flex-col gap-3 rounded-2xl border border-solid border-ds-border-neutral-subtle-default bg-ds-bg-neutral-subtle-default p-4">
            <div className="flex items-center justify-between gap-3">
              <div className="flex min-w-0 items-center gap-2">
                <MorphIcon icon={Brain} className="h-4 w-4 shrink-0" />
                <div className="min-w-0">
                  <h3 className="text-body-sm font-semibold text-ds-text-neutral-default-default">
                    Memory
                  </h3>
                  <p className="truncate text-xs text-ds-text-neutral-muted-default">
                    Lightweight notes ·{' '}
                    {memoryScope?.freshness || 'not synchronized'}
                  </p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                buttonContent="icon-only"
                buttonRadius="full"
                aria-label="Refresh Memory"
                disabled={memoryLoading}
                onClick={() => void loadMemoryScope()}
              >
                <MorphIcon
                  icon={memoryLoading ? Loader2 : RefreshCw}
                  className={cn('h-4 w-4', memoryLoading && 'animate-spin')}
                />
              </Button>
            </div>

            <div className="flex gap-1 rounded-xl bg-ds-bg-neutral-default-default p-1">
              {(['project', 'space', 'user'] as CloudMemoryScopeType[]).map(
                (scope) => (
                  <button
                    key={scope}
                    type="button"
                    className={cn(
                      'flex-1 rounded-lg px-2 py-1.5 text-xs capitalize',
                      memoryScopeType === scope
                        ? 'bg-ds-bg-neutral-subtle-default font-medium text-ds-text-neutral-default-default'
                        : 'text-ds-text-neutral-muted-default'
                    )}
                    onClick={() => setMemoryScopeType(scope)}
                  >
                    {scope}
                  </button>
                )
              )}
            </div>

            {memoryScope ? (
              <>
                <div className="flex items-center justify-between text-xs text-ds-text-neutral-muted-default">
                  <span>{memoryScope.entries.length} notes</span>
                  <span>
                    {memoryScope.current_token_count} /{' '}
                    {memoryScope.token_limit} tokens
                  </span>
                </div>
                <div className="flex max-h-52 flex-col gap-2 overflow-y-auto">
                  {memoryScope.entries.length ? (
                    memoryScope.entries.map((entry) => (
                      <article
                        key={entry.memory_id}
                        className="rounded-xl bg-ds-bg-neutral-default-default p-3"
                      >
                        <div className="mb-1 flex flex-wrap gap-2 text-[11px] text-ds-text-neutral-muted-default">
                          <span className="capitalize">{entry.kind}</span>
                          <span>{entry.source_trust.replaceAll('_', ' ')}</span>
                          {entry.pinned_by_user ? <span>Pinned</span> : null}
                          {entry.confirmed_by_user ? (
                            <span>Confirmed</span>
                          ) : null}
                        </div>
                        <p className="whitespace-pre-wrap text-xs text-ds-text-neutral-default-default">
                          {entry.content || 'Content unavailable'}
                        </p>
                      </article>
                    ))
                  ) : (
                    <p className="py-3 text-center text-xs text-ds-text-neutral-muted-default">
                      No saved Memory in this scope.
                    </p>
                  )}
                </div>
              </>
            ) : memoryError ? (
              <p className="text-xs text-ds-text-neutral-muted-default">
                {memoryError}
              </p>
            ) : (
              <p className="text-xs text-ds-text-neutral-muted-default">
                {memoryLoading ? 'Loading Memory…' : 'No active scope.'}
              </p>
            )}
            <p className="text-[11px] text-ds-text-neutral-muted-default">
              Manage or delete Memory in Eigent Desktop. Raw task History is not
              shown or editable.
            </p>
          </section>

          {/* Workspace Bundle review and Space environment */}
          <section className="flex flex-col gap-3 rounded-2xl border border-solid border-ds-border-neutral-subtle-default bg-ds-bg-neutral-subtle-default p-4">
            <div className="flex items-center justify-between gap-3">
              <div className="flex min-w-0 items-center gap-2">
                <MorphIcon icon={PackageOpen} className="h-4 w-4 shrink-0" />
                <div className="min-w-0">
                  <h3 className="text-body-sm font-semibold text-ds-text-neutral-default-default">
                    Workspace Bundle
                  </h3>
                  <p className="truncate text-xs text-ds-text-neutral-muted-default">
                    {environment?.installation
                      ? `${environment.installation.installed_revision_id} · ${environment.installation.state}`
                      : 'No Bundle selected for this Space'}
                  </p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                buttonContent="icon-only"
                buttonRadius="full"
                aria-label="Refresh Workspace Bundles"
                disabled={bundleLoading}
                onClick={() => void loadBundleState()}
              >
                {bundleLoading ? (
                  <MorphIcon icon={Loader2} className="h-4 w-4 animate-spin" />
                ) : (
                  <MorphIcon icon={RefreshCw} className="h-4 w-4" />
                )}
              </Button>
            </div>

            {bundles.length > 0 && (
              <div className="flex max-h-32 flex-col gap-1 overflow-y-auto">
                {bundles.map((bundle) => (
                  <button
                    key={bundle.id}
                    type="button"
                    className={cn(
                      'rounded-xl border border-solid px-3 py-2 text-left text-body-sm',
                      revision?.bundle_id === bundle.id
                        ? 'border-ds-border-information-default bg-ds-bg-neutral-default-default'
                        : 'border-transparent hover:bg-ds-bg-neutral-default-default'
                    )}
                    onClick={() => void selectBundle(bundle)}
                  >
                    <span className="block truncate font-medium text-ds-text-neutral-default-default">
                      {bundle.name}
                    </span>
                    <span className="block truncate text-xs text-ds-text-neutral-muted-default">
                      {bundle.package_name} · {bundle.visibility}
                    </span>
                  </button>
                ))}
              </div>
            )}

            {revision && (
              <div className="flex flex-col gap-2 rounded-xl bg-ds-bg-neutral-default-default p-3 text-xs text-ds-text-neutral-muted-default">
                <div className="flex items-center gap-2 text-body-sm font-medium text-ds-text-neutral-default-default">
                  <MorphIcon icon={ShieldCheck} className="h-4 w-4" />
                  Review{' '}
                  {bundles.find((item) => item.id === revision.bundle_id)
                    ?.package_name || revision.bundle_id}
                  @{revision.revision}
                </div>
                <p>
                  {requirements.assets} assets · {requirements.connectors}{' '}
                  connector slots · {requirements.localFolders} local folders ·{' '}
                  {requirements.executableActions} executable actions
                </p>
                <p>
                  Selecting a Bundle never grants connectors, local folders, or
                  scripts. Desktop must review and bind each local capability
                  before materialization.
                </p>
                {connectionRequirements.length ? (
                  <div className="flex flex-col gap-2">
                    <p className="font-medium text-ds-text-neutral-default-default">
                      Connection permissions
                    </p>
                    {connectionRequirements.map((slot) => {
                      const binding = environment?.bindings?.[slot.slotId];
                      const bound =
                        binding?.connector_id === slot.connectorId &&
                        Boolean(binding?.bound);
                      return (
                        <div
                          key={slot.slotId}
                          className="rounded-lg border border-solid border-ds-border-neutral-subtle-default px-2 py-2"
                        >
                          <div className="flex items-center justify-between gap-2">
                            <span className="font-mono text-ds-text-neutral-default-default">
                              {slot.slotId}
                            </span>
                            <span
                              className={
                                bound
                                  ? 'text-ds-text-status-completed-default-default'
                                  : 'text-ds-text-warning-default-default'
                              }
                            >
                              {bound ? 'Bound' : 'Not bound'}
                            </span>
                          </div>
                          <p>{slot.connectorId}</p>
                          <p>
                            Grants:{' '}
                            {slot.requiredGrants.length
                              ? slot.requiredGrants.join(', ')
                              : 'none'}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                ) : null}
                <Button
                  variant="outline"
                  tone="information"
                  size="sm"
                  buttonContent="text"
                  buttonRadius="full"
                  disabled={
                    bundleAction ||
                    !bundleProtocolReady ||
                    anotherProposalPending ||
                    reviewedBundleAction === 'installed' ||
                    reviewedBundleAction === 'unavailable'
                  }
                  onClick={() => void installReviewedBundle()}
                >
                  {bundleAction && (
                    <MorphIcon
                      icon={Loader2}
                      className="h-4 w-4 animate-spin"
                    />
                  )}
                  {reviewedBundleAction === 'confirm_proposal'
                    ? 'Confirm reviewed selection'
                    : anotherProposalPending
                      ? 'Resolve current proposal first'
                      : reviewedBundleAction === 'installed'
                        ? 'Installed'
                        : reviewedBundleAction === 'upgrade_proposal'
                          ? 'Create upgrade proposal'
                          : 'Create install proposal'}
                </Button>
                {!bundleProtocolReady ? (
                  <p className="text-ds-text-warning-default-default">
                    Bundle installation is disabled until the Cloud service
                    advertises the review and asset-integrity protocols.
                  </p>
                ) : null}
                {anotherProposalPending ? (
                  <p className="text-ds-text-warning-default-default">
                    A different revision is awaiting confirmation. Select that
                    revision to confirm it before creating another proposal.
                  </p>
                ) : null}
              </div>
            )}

            {bundleError && (
              <p className="text-xs text-ds-text-status-error-default-default">
                {bundleError}
              </p>
            )}
          </section>

          {/* Control buttons */}
          <div className="flex flex-col gap-2">
            <Button
              variant="outline"
              tone="neutral"
              size="md"
              buttonContent="text"
              buttonRadius="full"
              className="w-full !shadow-none"
              disabled={!ready || !!controlLoading}
              onClick={onSkip}
            >
              <MorphIcon icon={SkipForward} className="h-4 w-4" />
              <span className="remote-btn-label">Stop task</span>
            </Button>
            <Button
              variant="outline"
              tone="error"
              size="md"
              buttonContent="text"
              buttonRadius="full"
              className="w-full !shadow-none"
              disabled={!ready || !!controlLoading}
              onClick={onStop}
            >
              <MorphIcon icon={Ban} className="h-4 w-4" />
              <span className="remote-btn-label">Force stop</span>
            </Button>
            <Button
              variant="outline"
              tone="information"
              size="md"
              buttonContent="text"
              buttonRadius="full"
              className="w-full !shadow-none"
              disabled={!!controlLoading || session?.status !== 'active'}
              onClick={onExtend}
            >
              <MorphIcon icon={Clock3} className="h-4 w-4" />
              <span className="remote-btn-label">Extend link</span>
            </Button>
            <Button
              variant="outline"
              tone="warning"
              size="md"
              buttonContent="text"
              buttonRadius="full"
              className="w-full !shadow-none"
              disabled={!!controlLoading || session?.status !== 'active'}
              onClick={onRevoke}
            >
              <MorphIcon icon={ShieldX} className="h-4 w-4" />
              <span className="remote-btn-label">Revoke link</span>
            </Button>
          </div>
        </div>
      </div>
    </ResponsivePanel>
  );
}
